import React, { useEffect } from "react";
import Grid from "@mui/material/Grid";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";

import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";

import { MainLogo } from "../../components/Icons/MainLogo";
import { useNavigationMessageContext } from "context/NavigationMessageContext";
import Tip from "../../controls/UserGuide/Tip";
import Step from "controls/UserGuide/Step";
import Title from "controls/UserGuide/Title";
import { APP_NAME } from "../../constants";
import { useNavigate } from "react-router-dom";
import { Card } from "@mui/material";

interface TOCMenuItem {
    id: string,
    label: string,
    isSubMenu?: boolean,
}

const UserGuide = () => {
  const { setMessage } = useNavigationMessageContext();
  const navigate = useNavigate();

  const GUIDE_TOC = [
  { id: "dashboards-kpis", label: "Dashboards & KPIs" },
  { id: "corporate-profile", label: "Corporate Profile" },
  { id: "corporate-profile-account", label: "Corporate Account", isSubMenu: true },
  { id: "corporate-profile-profile", label: "Profile Information", isSubMenu: true },
  { id: "corporate-profile-admins", label: "Corporate Admins", isSubMenu: true },  
  { id: "managing-locations", label: "Managing Locations" },
  { id: "add-location", label: "Add a Location", isSubMenu: true},
  { id: "edit-location", label: "Edit Location Settings", isSubMenu: true}, 
  
  { id: "edit-location-address", label: "Edit Address and Phone", isSubMenu: true}, 
  { id: "edit-location-hours", label: "Edit Business Hours", isSubMenu: true}, 
  { id: "edit-location-options", label: "Edit Options and Amenities", isSubMenu: true}, 
  { id: "edit-location-doorlock", label: "Edit Door Lock Settings", isSubMenu: true}, 
  { id: "edit-location-hostadmins", label: "Edit Location Host Admins", isSubMenu: true}, 
  { id: "edit-location-closeforcleaning", label: "Close for Cleaning", isSubMenu: true}, 
  { id: "edit-location-clearrestroom", label: "Clear the Restroom", isSubMenu: true}, 
  { id: "edit-location-remove", label: "Remove Location", isSubMenu: true}, 

  { id: "location-visits", label: "Viewing Location Visits" },
  { id: "suspend-user", label: "Suspending/Banning Users" },
  { id: "poor-condition-reports", label: "Poor Condition Reports" },
];

  useEffect(() => {
    setMessage("Business User Guide");
    return () => setMessage(undefined);
  }, [setMessage]);

  return (
    <DashboardLayout>
      <DashboardNavbar fixedNavbar={false} />
      <MDBox py={6}>
            <Grid container spacing={4}>
            {/* LEFT: Sticky TOC */}
            <Grid item xs={12} lg={2}>
                <MDBox sx={{position: "sticky",top: 96,}}>
                    <MDBox mb={6}>
                        <MDTypography variant="h5" fontWeight="bold" mb={2}>
                            Quick Links
                        </MDTypography>
                        <MDBox component="ul" sx={{ pl: 3, m: 0 }}>
                            {GUIDE_TOC.map((item) => (
                            <li key={item.id} style={item.isSubMenu ? {marginLeft: 10} : {}}>
                                <MDTypography
                                component="a"          
                                href={`#${item.id}`}
                                variant={item.isSubMenu ? "body2" : "h6"}
                                sx={{ cursor: "pointer" }}
                                >
                                {item.label}
                                </MDTypography>
                            </li>
                            ))}
                        </MDBox>
                                <MDTypography
                                component="button"          
                                onClick={()=> navigate('/support')}
                                variant={"h6"}
                                sx={{ cursor: "pointer", border: 'none', marginLeft:4, marginTop:2 }}
                                >
                                Get More Support
                                </MDTypography>
                    </MDBox>
                </MDBox>
            </Grid>

            {/* RIGHT: Guide Content */}
            <Grid item xs={12} lg={10} xl={8}>

            {/* HEADER */}
            <MDBox textAlign="center" mb={6}>
              <MainLogo style={{ height: 120 }} />
              <MDTypography
                variant="h3"
                fontWeight={700}
                mt={2}
                mb={1}
              >
                Pappa Squat Business User and Host Guide
              </MDTypography>
              <MDTypography variant="body1" color="text.secondary">
                Turn Your Restroom Into an Asset
              </MDTypography>
            </MDBox>

              <Card sx={{padding:3}}>
            {/* INTRO */}
            <MDBox mb={4}>
              <MDTypography variant="h5" fontWeight="bold">
                Welcome
              </MDTypography>
              <MDTypography variant="body2">
                Thank you for chosing to list your business on the {APP_NAME} platform! We are here to ensure you have the best experience possible. If you have any question feel free to contact us.
                <br />This guide walks hosts through everything needed to manage
                locations, monitor activity, and operate efficiently using
                Pappa Squat. All features are available through the mobile app
                and the web dashboard.
              </MDTypography>
            </MDBox>


            {/* GUIDE SECTION (REUSABLE) */}
            <MDBox mb={4}>
                <Title id="dashboards-kpis" title="Dashboards & KPIs" subTitle="After signing in, you'll land on the Host Dashboard. This screen provides a real-time overview of how your restrooms are performing." />
                <Step id="dashboard-metrics" title="Key Metrics Include:" message="Each KPI on the dashboard provides a different metric based on live data. Click each KPI to view more details or go to the location page for a particular location." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ul>
                        <li>Total visits across all locations</li>
                        <li>Visits by location</li>
                        <li>Live Status of each location</li>
                        <li>Live ROI Calculations</li>
                        <li>Cleaning and Condition metrics</li>
                    </ul>
                </MDTypography>
                <Tip title="More Data" message="Each KPI on the dashboard can be clicked at the bottom to reveal more detailed information about each location. Click on the detailed information to be taken to the associated page to get even more insights!" />
            </MDBox>
            <MDBox mb={4}>
                <Title id="corporate-profile" title="Corporate Profile" subTitle={`Every business in the ${APP_NAME} network has a corporate profile account. You can edit these details on the profile page.`} />
                <Step id="corporate-profile-account" title="Corporate Account" message="This is the corporate contact information for the company. This may or may not be the same as your hosting location. If you have mulitple locations, this is the parent company information." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Profile' from the menu</li>
                        <li>In the top right corner of the 'Corporate Account' section, Click the pencil icon to edit</li>
                        <li>Update business name and other information as desired</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                <Step id="corporate-profile-profile" title="Profile Information:" message="This is the email and phone number associated with your corporate user account. You may use this account to use the app as a regular user and to manage your locations." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Profile' from the menu</li>
                        <li>In the top right corner of the 'Profile Information' section, Click the pencil icon to edit</li>
                        <li>Update your name and other information as desired</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                
                <Step id="corporate-profile-admins" title="Corporate Admins:" message={`You can add corporate admins. ${APP_NAME} app users associated with these email addresses have access to all locations and some corporate settings. Please note ${APP_NAME} does not validate these email adresses, nor any associated ${APP_NAME} accounts.`} />
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click 'Profile' from the menu</li>
                        <li>View the 'Corporate Admins' section</li>                        
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The corporate admins currently saved for the company are displayed in a grid style format in the 'Corporate Admins' section.
                </MDTypography>                    

                <MDTypography variant="h6" color="text.secondary" pl={4}>To add a corporate admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click 'Profile' from the menu</li>
                        <li>In the 'Corporate Admins' section, Click the 'Add Admin' button</li>
                        <li>Enter a valid email address</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The newly saved admin record will appear in the list.
                </MDTypography>
                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To edit a corporate admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click the 'Edit' button to the right of the corporate admin</li>
                        <li>Update the email with a valid email address</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary"pl={4} mb={2}>
                    The updated admin record will appear in the list.
                </MDTypography>                    
                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To delete a corporate admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click the 'Remove' button to the right of the corporate admin</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The admin record will be removed from the list and any associated accounts will no longer have access to the business.
                </MDTypography>                    

                <Tip color="error" title="Please Note" message="Corporate Admin is NOT the same thing as Location Host Admins. Corporate Admins have access to all locations and all corporate settings - Location Host Admins only have access to the specific location(s). - to Manage your Location Host Admins, to to the location page." />
            </MDBox>


            <MDBox mb={4}>
                <Title id="managing-locations" title="Managing Locations" subTitle="Each location represents a physical restroom at your business. You have full control of your restroom and are able to manage all your location settings. You can view metrics, close for cleaning, check user visit activity, or even suspend or ban a user." />
                <Step id="add-location" title="Add a Location" message="Adding a location is easy! Our simple wizard will guide you through the process of setting up a location. When adding a location, remember that all settings can be adjusted later in the location settings page." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click 'Add Location'</li>
                        <li>Enter business name and address</li>
                        <li>Enter hours, options, and amentities</li>
                        <li>Select a Hosting Plan</li>
                        <li>Save the Location</li>
                    </ol>
                </MDTypography>
                <Step title="View Location Metrics" message="View activity, manage settings and more on the location page." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol style={{marginBottom:6}}>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to view that location</li>
                    </ol>
                    <ul style={{marginLeft:20}}>
                        <li>View location Metrics</li>
                        <li>Click 'View Location Activity' to view visits by users</li>
                        <li>Click 'View Poor Condition Reports' to view reports left by users</li>
                        <li>Click 'Manage Suspended Users' to view or manage suspended users</li>
                        <li>Click 'Clear Restroom' if the app thinks there is a user in your restroom but there is not</li>
                        <li>Click 'Close for Cleaning' if you need to temporarily close for cleaning</li>
                    </ul>
                </MDTypography>
                <Tip message="You can 'Close for Cleaning' if you need to close for any reason at all! This location will not show to users on the map during this time." />
            </MDBox>

            <MDBox mb={4}>
                <Step id="edit-location" title="Editing Location Settings" message="You can change hosting settings for your location on the location settings page." />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Adjust any settings as you wish</li>
                    </ol>
                </MDTypography>

                <Step id="edit-location-address" title="Edit Location Address & Phone Number" />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                The address currently saved for the location is displayed in the input controls at the top of the page, in the 'Business Location Information' section.
                    To edit,
                </MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Change the values for address as needed</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>                    
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                The new address will be verified as when you added the location.
                </MDTypography>


                <Step id="edit-location-hours" title="Edit Business Hours" />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Scroll to the 'Business Hours' section to view currently saved hours</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The business hours currently saved for the location are displayed in the 'Business Hours' section.
                    To edit,
                </MDTypography>                    
                <MDTypography fontWeight="medium" variant="h6">Select 'Open 24 Hours' option</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>If the location is open 24 hours a day, ensure this is selected. If not, click the toggle to select specific business hours.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">Select Specific Business Hours (Not Open 24/7)</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={4}>
                    <ol>
                        <li>When open 24 hours is turned off, you will see options for each day of the week. For each day, select the business hours for the location. If the location is closed for that day, simply click the 'Closed' toggle for that day.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
            <Tip message="You can manage everything from either the app or the web — changes sync instantly." />
            <MDBox p={2} />
                <Step id="edit-location-options" title="Edit Options & Amenities" />
                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Scroll to the 'Options & Amenities' section</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The amenities currently saved for the location are indicated by a solid blue color button in the 'Options & Amenities' section.
                    To edit,
                </MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click any amenity button to toggle the option on/off.</li>                        
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    If the button is a solid blue color, the options is turned on. If the button is an outlined white color, the options turned off.
                </MDTypography>                    



                <Step id="edit-location-doorlock" title="Edit Door Lock Settings" />

                <MDTypography variant="body2" color="text.secondary" mt={-2} pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Scroll to the 'Door Lock Settings' section</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The door lock settings currently saved for the location are displayed in the 'Door Lock Settings' section.
                    To edit,
                </MDTypography>                    
                <MDTypography fontWeight="medium" variant="h6">'Require Door Lock for Entry' option</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>If the location restroom requires a door lock code for entry, ensure this is selected. If not, click the toggle to turn off door lock requirements.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">Door Lock Code</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>When a door lock is required, you will see an option for Door Lock Code. Enter a door lock code that unlocks the restroom door at this location. This should be a numerical code the user will enter into the keypad on the door lock to gain access to the restroom.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">Time per Visit</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>When a door lock is required, you will see an option for Time per Visit (minutes). This is time allowed per visit. The user will get additional time if requested. Enter how many minutes a normal visit should be at this location.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">'Door Lock Uses QR Reader' option</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>When a door lock is required, you will see an option for 'Door Lock Uses QR Reader'. If your door lock has a QR Reader on it, you can enable this for users. The app will present the user the QR code to use for entry.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">'Always Require Door Code' option</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={4}>
                    <ol>
                        <li>When a door lock is required, you will see an option for 'Always Require Door Code'. This means the location's restroom is always locked and is accessible with a door lock code. Toggle this option 'off' to lock the restroom during specific hours.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography fontWeight="medium" variant="h6">Specific Door Lock Hours (Not Always Require Door Lock Code)</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={4}>
                    <ol>
                        <li>When 'Always Require Door Code' is turned off, you will see options for each day of the week. For each day, select the hours the restroom will be locked for the location. If the location is locked all day for that day, simply click the 'Locked All Day' toggle for that day. If the location is not locked that day, simply click the 'Not Locked' toggle for that day.</li>
                        <li>Click 'Save Changes' button at the bottom of the page</li>
                    </ol>
                </MDTypography>

            <Tip message="You can make multiple changes and save once when you are finished." />
            <MDBox p={2} />
                <Step id="edit-location-hostadmins" title="Edit Location Host Admins" />
                <MDTypography variant="body2" mt={-2}>You can add users as admins for this location. Be aware, all {APP_NAME} user accounts associated with listed email addresses will have access to this location and its settings.</MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Scroll to the 'Location Host Admins' section</li>                        
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The location host admins currently saved for the location are displayed in a grid style format in the 'Location Host Admins' section.
                </MDTypography>                    
                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To add a location host admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click 'Add Admin'</li>
                        <li>Enter a valid email address</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The newly saved admin record will appear in the list.
                </MDTypography>                    
                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To edit a location host admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click the 'Edit' button to the right of the location host admin</li>
                        <li>Update the email with a valid email address</li>
                        <li>Click 'Save'</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary"pl={4} mb={2}>
                    The updated admin record will appear in the list.
                </MDTypography>                    
                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To delete a location host admin,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    <ol>
                        <li>Click the 'Remove' button to the right of the location host admin</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0} pl={4} mb={2}>
                    The admin record will be removed from the list and any associated accounts will no longer have access to the location.
                </MDTypography>                    

            <Tip color="error" title="Please Note" message="Location Host Admin is NOT the same thing as your Corporate Admins. Location Host Admins only have access to the specific location. Corporate Admins have access to all of your locations and corporate settings. - to Manage your Corporate Admins, to to your profile page." />

            </MDBox>
            <MDBox mb={4}>
                <Step id="edit-location-closeforcleaning" title="Close for Cleaning" message="You can temporarily close your hosting location as long as you need. You may do this to clean or for any reason you see neccessary." />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click the 'Close for Cleaning' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4}>
                    Repeat these steps to Re-Open after Cleaning. Remember you will show up on the map, but not be able to host users if you are closed for cleaning.
                </MDTypography>    
            </MDBox>
            <MDBox mb={4}>
                <Step id="edit-location-clearrestroom" title="Clear Restroom" message={`The ${APP_NAME} app works on a visit que system to host users. Basically, users can get in line if the restroom is in use. Due to user error or system failure, sometimes it may be necessary to clear a visit from your restroom to allow the next users.`} />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click the 'Clear Restroom' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    Your restroom is now clear of all bad user sessions and users will no longer see a line and will be allowed to enter.
                </MDTypography>    
                <Tip color="error" title="Please Note" message={`Do this only if you are sure no users are in your restroom. If a user is using the restroom when this is done, other users will be able to access the locked restroom. ${APP_NAME} and any affliates are not responsible for any claims of any kind due to this function. Do Not Clear the Restroom if a user is using the restroom.`} />
            </MDBox>
            <MDBox mb={4}>
                <Step id="edit-location-remove" title="Remove Location" message="You are able to remove a location at any time. If removed, the location will not be available to app users." />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Location Settings' button at the top of the page</li>
                        <li>Scroll to the bottom of the page</li>
                        <li>Click the 'Remove Location' button and follow the prompts</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The location will be removed and will no longer be able to accept users. If this is done by accident, please contact us to restore this location so you can maintain all the visit history and user ratings.
                </MDTypography> 
            </MDBox>
            <MDBox mb={4}>
                <Title id='location-visits' title="Viewing Location Visits" subTitle="You can view user visit history for each location. We provide the latest 100 visits for viewing and you can request additional data." />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'View Location Activity' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    This will show a list of the most recent 100 visits for the selected location. Click each visit to see details of the visit. You can rate the user by entering a note (optional) and clicking the desired star on the rating control. You can also suspend or ban the user associated with the visit. 
                </MDTypography>    
            </MDBox>
            <MDBox mb={4}>
                <Title id="suspend-user" title="Suspend or Ban Users" subTitle="You are able to suspend or ban users on the app. If a user is banned or suspended from a location, that location will not show on the map for that user. You can lift any suspensions or bans at a later date on the location page." />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Suspended Users' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    This will show a list of app users that have been suspended or banned from the location.
                </MDTypography>    

                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To lift a suspension or ban,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'Manage Suspended Users' button at the top of the page</li>
                        <li>Find and Click on the user you wish to manage</li>
                        <li>When details are displayed, click 'Lift Suspension / Lift Ban' button</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The user is no longer suspended or banned from using this location.
                </MDTypography>    

                <MDTypography variant="h6" color="text.secondary" mt={0} pl={4}>To suspend or ban a user,</MDTypography>                    
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on the Location where the visit happened</li>
                        <li>Click 'View Location Activity' button at the top of the page</li>
                        <li>Find and Click on the Visit associated with the user you wish to suspend or ban</li>
                        <li>When details are displayed, click 'Suspend / Ban User' button</li>
                        <li>Complete the form and Click 'Save'</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    The user is now suspended or banned from using this location. You can lift this suspsension any time you wish in the location settings page.
                </MDTypography>    
            </MDBox>


            <MDBox mb={4}>
                <Title id="poor-condition-reports" title="Poor Condition Reports" subTitle="All users are required to take a photo of the restroom prior to ending the visit. Within 1 minute of staring the visit, users are able to report poor conditions at a location. The user prior to this will be looked at to see if they left the restroom in good condition." />
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    <ol>
                        <li>Click 'Locations' from the menu</li>
                        <li>Click on any Location to manage that location</li>
                        <li>Click 'View Poort Condition Reports' button at the top of the page</li>
                    </ol>
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" pl={4} mb={2}>
                    This will show a list of the most recent 100 user submitted poor condition reports. If needed, please close for cleaning and resume hosting when the restroom conditions have been improved.
                </MDTypography>    
            </MDBox>


            {/* SUPPORT CTA */}
            <MDBox
              p={4}
              borderRadius="lg"
              textAlign="center"
              sx={{
                backgroundColor: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" mb={1}>
                Need More Help?
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" mb={3}>
                Our team is here to support you if you have questions or need
                assistance.
              </MDTypography>
              <MDButton
                variant="gradient"
                color="info"
                component="button"
                onClick={() => navigate("/support")}
              >
                Contact Support
              </MDButton>
            </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
};

export default UserGuide;
